/**
 * Edit Role Modal Component
 * Allows editing and deleting existing roles
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Collapse,
  Space,
  Typography,
  Row,
  Col,
  Divider,
  Tag,
  Checkbox,
  Button,
} from 'antd';
import { FaShieldAlt, FaFolderOpen } from 'react-icons/fa';
import { DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { Permission, InternalPermissionState } from '@/types/permissions.types';
import { PermissionCategories } from '@/features/settings/constants/permissions';
import { IAppRole } from '@/features/users/types';

const { Text } = Typography;
const { Panel } = Collapse;

interface EditRoleModalProps {
  open: boolean;
  role: IAppRole | null;
  permissionState: InternalPermissionState;
  loading?: boolean;
  deleteLoading?: boolean;
  onCancel: () => void;
  onUpdate: (data: {
    roleId: string;
    roleName: string;
    permissions: Permission[];
    permissionsJson: string;
  }) => void;
  onDelete: (roleId: string) => void;
}

export const EditRoleModal: React.FC<EditRoleModalProps> = ({
  open,
  role,
  permissionState,
  loading = false,
  deleteLoading = false,
  onCancel,
  onUpdate,
  onDelete,
}) => {
  const [form] = Form.useForm();
  const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>([]);

  // Initialize form and permissions when role changes
  useEffect(() => {
    if (role && permissionState[role.dogma_approleid]) {
      form.setFieldsValue({
        roleName: role.dogma_name,
      });

      // Get selected permissions from permission state
      const rolePermissions = permissionState[role.dogma_approleid];
      if (!rolePermissions) return;

      const selected: Permission[] = [];

      Object.entries(rolePermissions).forEach(([permission, state]) => {
        if (state.enabled) {
          selected.push(permission as Permission);
        }
      });

      setSelectedPermissions(selected);
    }
  }, [role, permissionState, form]);

  const handlePermissionToggle = (permission: Permission, checked: boolean) => {
    setSelectedPermissions((prev) => {
      if (checked) {
        return [...prev, permission];
      } else {
        return prev.filter((p) => p !== permission);
      }
    });
  };

  const handleCategoryToggle = (categoryPermissions: Permission[], checked: boolean) => {
    setSelectedPermissions((prev) => {
      if (checked) {
        const newPermissions = [...prev];
        categoryPermissions.forEach((perm) => {
          if (!newPermissions.includes(perm)) {
            newPermissions.push(perm);
          }
        });
        return newPermissions;
      } else {
        return prev.filter((p) => !categoryPermissions.includes(p));
      }
    });
  };

  const handleUpdate = async () => {
    try {
      const values = await form.validateFields();

      if (!role) return;

      // Generate permissions JSON - NEW FLAT STRUCTURE: { "permission": true }
      const permissionsJson: Record<string, boolean> = {};

      PermissionCategories.forEach((category) => {
        category.permissions.forEach((perm) => {
          permissionsJson[perm.id] = selectedPermissions.includes(perm.id);
        });
      });

      onUpdate({
        roleId: role.dogma_approleid,
        roleName: values.roleName,
        permissions: selectedPermissions,
        permissionsJson: JSON.stringify(permissionsJson),
      });
    } catch (error) {
    }
  };

  const handleDelete = () => {
    if (!role) return;

    Modal.confirm({
      title: 'Delete Role',
      content: `Are you sure you want to delete the role "${role.dogma_name}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        onDelete(role.dogma_approleid);
      },
    });
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const isCategoryFullySelected = (categoryPermissions: Permission[]): boolean => {
    return categoryPermissions.every((perm) => selectedPermissions.includes(perm));
  };

  const isCategoryPartiallySelected = (categoryPermissions: Permission[]): boolean => {
    const selected = categoryPermissions.filter((perm) => selectedPermissions.includes(perm));
    return selected.length > 0 && selected.length < categoryPermissions.length;
  };

  if (!role) return null;

  return (
    <Modal
      title={
        <Space>
          <FaShieldAlt style={{ color: '#1890ff' }} />
          <span>Edit Role</span>
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      width={800}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      footer={[
        <Button
          key="delete"
          danger
          icon={<DeleteOutlined />}
          onClick={handleDelete}
          loading={deleteLoading}
        >
          Delete Role
        </Button>,
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="update"
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleUpdate}
          loading={loading}
        >
          Update Role
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" autoComplete="off">
        <Form.Item
          name="roleName"
          label="Role Name"
          rules={[
            { required: true, message: 'Please enter a role name' },
            { min: 3, message: 'Role name must be at least 3 characters' },
            { max: 100, message: 'Role name must not exceed 100 characters' },
          ]}
        >
          <Input placeholder="e.g., Custom Sales Manager" size="large" />
        </Form.Item>

        <Divider>Permissions ({selectedPermissions.length} selected)</Divider>

        <Collapse
          accordion={false}
          defaultActiveKey={PermissionCategories.map((cat) => cat.id)}
          style={{ background: '#fafafa' }}
        >
          {PermissionCategories.map((category) => {
            const categoryPermissionIds = category.permissions.map((p) => p.id);
            const isFullySelected = isCategoryFullySelected(categoryPermissionIds);
            const isPartiallySelected = isCategoryPartiallySelected(categoryPermissionIds);
            const selectedCount = categoryPermissionIds.filter((p) =>
              selectedPermissions.includes(p)
            ).length;

            return (
              <Panel
                key={category.id}
                header={
                  <div
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Space>
                      <Checkbox
                        checked={isFullySelected}
                        indeterminate={isPartiallySelected}
                        onChange={(e) =>
                          handleCategoryToggle(categoryPermissionIds, e.target.checked)
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                      <FaFolderOpen style={{ color: '#8b1538' }} />
                      <Text strong>{category.name}</Text>
                    </Space>
                    <Tag color={selectedCount > 0 ? 'blue' : 'default'}>
                      {selectedCount}/{category.permissions.length}
                    </Tag>
                  </div>
                }
              >
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {category.permissions.map((perm) => (
                    <Row key={perm.id} gutter={8}>
                      <Col span={24}>
                        <Checkbox
                          checked={selectedPermissions.includes(perm.id)}
                          onChange={(e) => handlePermissionToggle(perm.id, e.target.checked)}
                        >
                          <Space direction="vertical" size={0}>
                            <Text strong style={{ fontSize: 13 }}>
                              {perm.label}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {perm.description}
                            </Text>
                          </Space>
                        </Checkbox>
                      </Col>
                    </Row>
                  ))}
                </Space>
              </Panel>
            );
          })}
        </Collapse>
      </Form>
    </Modal>
  );
};

export default EditRoleModal;
